import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Plus, User, Clock, FileText, X, ChevronLeft, ChevronRight, Settings, CheckCircle, Link2, AlertCircle, Users, RefreshCw, Search } from 'lucide-react';
import './GoogleCalendarView.css';
import DoctorScheduleModal from './DoctorScheduleModal';

import { API_URL } from '../config/api';

function GoogleCalendarView() {
    const [user, setUser] = useState(null);

    const [isDoctor, setIsDoctor] = useState(false);
    const [myDoctorId, setMyDoctorId] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
    const [creatingAppointment, setCreatingAppointment] = useState(false);
    const [creatingBlock, setCreatingBlock] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [googleConnectionStatus, setGoogleConnectionStatus] = useState({});
    const [connectingDoctor, setConnectingDoctor] = useState(null);
    const [patients, setPatients] = useState([]);
    const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
    const [currentDate, setCurrentDate] = useState(new Date()); // For day view
    const [viewMode, setViewMode] = useState('week'); // 'day', 'week', or 'month'

    // Persist view mode preference per user
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        const userData = localStorage.getItem('user');
        const userId = userData ? JSON.parse(userData).id : 'default';
        localStorage.setItem(`calendarViewMode_${userId}`, mode);
    };

    // Initial state for new appointment form
    const initialAppointmentState = {
        doctor_id: '',
        patient_id: '',
        patient_name: '',
        patient_email: '',
        date: '',
        start_time: '12:00',
        end_time: '12:30',
        appointment_type: 'primera_vez', // 'primera_vez' or 'subsecuente'
        office: '', // Consultorio
        reason: '',
        notes: '',
        is_guest: false,
        guest_name: '',
        guest_phone: ''
    };

    const [newAppointment, setNewAppointment] = useState(initialAppointmentState);
    const [isAppointmentPanelMinimized, setIsAppointmentPanelMinimized] = useState(false);
    const [selectedDoctorSchedule, setSelectedDoctorSchedule] = useState([]);
    const [selectedEventDetails, setSelectedEventDetails] = useState(null); // For event detail modal
    const [selectedPatientSummary, setSelectedPatientSummary] = useState(null); // For patient history info
    const [patientSearch, setPatientSearch] = useState('');
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);

    // Self-link state (for doctors not yet linked to a doctor profile)
    const [unlinkedDoctors, setUnlinkedDoctors] = useState([]);
    const [selfLinkLoading, setSelfLinkLoading] = useState(false);
    const [selfLinkSpecialty, setSelfLinkSpecialty] = useState('');
    const [selfLinkMode, setSelfLinkMode] = useState('choose'); // 'choose' or 'create'

    // Reset appointment form to initial state
    const resetAppointmentForm = () => {
        setNewAppointment(initialAppointmentState);
        setSelectedPatientSummary(null);
        setSelectedDoctorSchedule([]);
        setIsAppointmentPanelMinimized(false);
        setPatientSearch('');
        setShowPatientDropdown(false);
    };

    // State for Schedule Block Modal
    const [showBlockModal, setShowBlockModal] = useState(false);
    const initialBlockState = {
        doctor_id: '',
        start_date: '',
        end_date: '',
        reason: 'Vacaciones/Permiso'
    };
    const [newBlock, setNewBlock] = useState(initialBlockState);
    const [blockConflictInfo, setBlockConflictInfo] = useState(null);

    const resetBlockForm = () => {
        setNewBlock({
            ...initialBlockState,
            doctor_id: isDoctor ? myDoctorId : (selectedDoctor !== 'all' ? selectedDoctor : '')
        });
    };

    function getWeekStart(date) {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    }

    const fetchDoctorSchedule = async (doctorId) => {
        if (!doctorId) {
            setSelectedDoctorSchedule([]);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/doctors/${doctorId}/schedule`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const schedules = Array.isArray(data) ? data : (data.data || data.schedules || []);
                setSelectedDoctorSchedule(schedules);
            }
        } catch (err) {
            console.error('Error fetching doctor schedule:', err);
            setSelectedDoctorSchedule([]);
        }
    };

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);

            const isDoc = (parsedUser.role?.id === 2 || parsedUser.role_id === 2 || (parsedUser.role?.name || '').toLowerCase().includes('medico'));
            setIsDoctor(isDoc);

            // Default view mode from localStorage
            const savedViewMode = localStorage.getItem(`calendarViewMode_${parsedUser.id}`) || 'week';
            setViewMode(savedViewMode);
        }

        const init = async () => {
            // Setup absolute safety timeout to remove spinner even if backend hangs
            const safetyTimeout = setTimeout(() => {
                setInitialLoading(false);
                console.log('Calendar initialization Safety Timeout reached');
            }, 5000);

            try {
                // Initialize core data in parallel
                await Promise.all([
                    fetchDoctors(),
                    fetchGoogleConnectionStatus()
                ]);

                // Patients are less critical for initial view, fetch in background
                fetchPatients();

                setInitialLoading(false);
                clearTimeout(safetyTimeout);
            } catch (error) {
                console.error('Error during calendar initialization:', error);
                setInitialLoading(false);
                clearTimeout(safetyTimeout);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (selectedDoctor) {
            fetchEvents();
        }
    }, [selectedDoctor, currentWeekStart, currentDate, viewMode]);

    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/doctors?linked=1`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const doctorsList = Array.isArray(data) ? data : (data.data || []);

                // Add "All Doctors" option for non-doctors
                const finalDoctors = [{ id: 'all', name: 'Todos los Médicos' }, ...doctorsList];
                setDoctors(finalDoctors);

                // Handle initial doctor selection
                const userData = localStorage.getItem('user');
                if (userData) {
                    const parsedUser = JSON.parse(userData);
                    const isDoc = (parsedUser.role?.id === 2 || parsedUser.role_id === 2 || (parsedUser.role?.name || '').toLowerCase().includes('medico'));

                    if (isDoc) {
                        const myDoc = doctorsList.find(d => d.user_id === parsedUser.id);
                        if (myDoc) {
                            setMyDoctorId(myDoc.id);
                            setSelectedDoctor(myDoc.id.toString());
                        }
                    } else {
                        setSelectedDoctor('all');
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching doctors:', err);
        }
    };

    const fetchPatients = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPatients(Array.isArray(data) ? data : (data.data || []));
            }
        } catch (err) {
            console.error('Error fetching patients:', err);
        }
    };

    const fetchPatientSummary = async (patientId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients/${patientId}/summary`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSelectedPatientSummary(data);
            }
        } catch (err) {
            console.error('Error fetching patient summary:', err);
        }
    };


    const fetchGoogleConnectionStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/google/status/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const text = await response.text();
                if (text && text.startsWith('{')) {
                    const data = JSON.parse(text);
                    if (data.success && Array.isArray(data.doctors)) {
                        const statusMap = {};
                        data.doctors.forEach(doc => {
                            statusMap[doc.doctor_id] = doc.connected;
                        });
                        setGoogleConnectionStatus(statusMap);
                    }
                }
            }
        } catch (err) {
            console.log('Google Calendar status not available');
        }
    };

    const getDoctorColor = (index) => {
        const colors = [
            'linear-gradient(135deg, #4285f4 0%, #1a73e8 100%)', // Blue
            'linear-gradient(135deg, #00BBBF 0%, #00A5A8 100%)', // Emerald
            'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', // Violet
            'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Amber
            'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', // Pink
        ];
        return colors[index % colors.length];
    };

    const fetchEvents = async () => {
        if (!selectedDoctor) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let startDate, endDate;

            // Helper function to format date as YYYY-MM-DD in local timezone
            const formatLocalDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Calculate date range based on viewMode (using local timezone)
            if (viewMode === 'day') {
                startDate = formatLocalDate(currentDate);
                endDate = startDate;
            } else if (viewMode === 'week') {
                startDate = formatLocalDate(currentWeekStart);
                const weekEnd = new Date(currentWeekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);
                endDate = formatLocalDate(weekEnd);
            } else if (viewMode === 'month') {
                const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                startDate = formatLocalDate(monthStart);
                endDate = formatLocalDate(monthEnd);
            }

            console.log('Fetching events for range:', startDate, 'to', endDate); // Debug log

            let allEvents = [];

            if (selectedDoctor === 'all') {
                // Fetch for ALL doctors (connected and offline ?" backend returns local events too)
                const allDoctors = doctors.filter(d => d.id !== 'all');

                const promises = allDoctors.map((doc, index) =>
                    fetch(
                        `${API_URL}/google/events?doctor_id=${doc.id}&start_date=${startDate}&end_date=${endDate}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    ).then(async res => {
                        if (res.ok) {
                            const data = await res.json();
                            return (data.events || []).map(ev => ({
                                ...ev,
                                doctorName: doc.name,
                                doctorId: doc.id, // Store original doctor ID
                                color: getDoctorColor(index)
                            }));
                        }
                        return [];
                    }).catch(err => {
                        console.error(`Error fetching events for doctor ${doc.name}:`, err);
                        return [];
                    })
                );

                const results = await Promise.all(promises);
                allEvents = results.flat();
            } else {
                // Fetch for single doctor (Modified for Hybrid System)
                // Even if not connected, we fetch to get local events

                const response = await fetch(
                    `${API_URL}/google/events?doctor_id=${selectedDoctor}&start_date=${startDate}&end_date=${endDate}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );

                if (response.ok) {
                    const data = await response.json();
                    allEvents = (data.events || []).map(ev => ({
                        ...ev,
                        color: getDoctorColor(0) // Default blue
                    }));
                } else {
                    // Handle error or offline mode explicitly if needed
                    console.error('Error fetching events');
                    setEvents([]);
                }
            }

            setEvents(allEvents);

        } catch (err) {
            console.error('Error fetching events:', err);
        } finally {
            setLoading(false);
        }
    };

    const connectDoctorToGoogle = async (doctorId) => {
        try {
            setConnectingDoctor(doctorId);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/google/auth?doctor_id=${doctorId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.auth_url) {
                    window.location.href = data.auth_url;
                }
            }
        } catch (err) {
            console.error('Error connecting to Google:', err);
        } finally {
            setConnectingDoctor(null);
        }
    };

    const disconnectDoctorFromGoogle = async (doctorId) => {
        console.log('Disconnect clicked for doctor:', doctorId);

        const doctorName = doctors.find(d => d.id.toString() === doctorId.toString())?.name || 'este médico';

        const confirmed = window.confirm(`¿Estás seguro de desconectar a ${doctorName} de Google Calendar?\n\nLas citas existentes en Google Calendar no se eliminarán.`);

        console.log('Confirm result:', confirmed);

        if (!confirmed) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            console.log('Making disconnect request...');

            const response = await fetch(`${API_URL}/google/disconnect?doctor_id=${doctorId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            console.log('Response status:', response.status);

            if (response.ok) {
                // Update local state
                setGoogleConnectionStatus(prev => ({
                    ...prev,
                    [doctorId]: false
                }));
                setEvents([]);
                alert('? Desconectado de Google Calendar');
            } else {
                const errorData = await response.text();
                console.error('Disconnect error response:', errorData);
                alert('Error al desconectar: ' + response.status);
            }
        } catch (err) {
            console.error('Error disconnecting from Google:', err);
            alert('Error al desconectar: ' + err.message);
        }
    };

    const cancelAppointment = async (eventId, eventTitle, doctorId) => {
        // Detect if this is a block event
        const isBlockEvent = (eventTitle || '').toUpperCase().includes('BLOQUEO');
        const confirmMsg = isBlockEvent
            ? `¿Estás seguro de cancelar este bloqueo?\n\n"${eventTitle || 'Sin título'}"\n\nEsta acción no se puede deshacer.`
            : `¿Estás seguro de cancelar esta cita?\n\n"${eventTitle || 'Sin título'}"\n\nEsta acción no se puede deshacer.`;

        if (!window.confirm(confirmMsg)) {
            return;
        }

        // Use the provided doctorId, fallback to selectedDoctor
        const targetDoctorId = doctorId || selectedDoctor;

        if (!targetDoctorId || targetDoctorId === 'all') {
            alert('Error: No se pudo identificar el médico de esta cita');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            let response;

            // Check if this is a local-only appointment (ID starts with "local_") or a block event
            if (typeof eventId === 'string' && eventId.startsWith('local_')) {
                const localId = eventId.replace('local_', '');
                // Cancel via local appointments endpoint
                response = await fetch(`${API_URL}/appointments/${localId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } else if (isBlockEvent) {
                // Block events are LOCAL ONLY — route directly to appointments endpoint
                // eventId for blocks in the UI is usually 'local_ID' or the Google ID if synced
                const localId = typeof eventId === 'string' && eventId.startsWith('local_')
                    ? eventId.replace('local_', '')
                    : eventId;

                response = await fetch(`${API_URL}/appointments/${localId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } else {
                // Cancel via Google Calendar endpoint (also cancels locally)
                response = await fetch(`${API_URL}/google/events/${eventId}?doctor_id=${targetDoctorId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            if (response.ok) {
                // Remove from local events
                setEvents(prev => prev.filter(e => e.id !== eventId));
                alert(isBlockEvent ? '✅ Bloqueo cancelado exitosamente' : '✅ Cita cancelada exitosamente');
                // Refresh events to reflect the change
                fetchEvents();
            } else {
                const error = await response.json();
                alert('Error al cancelar: ' + (error.message || 'Error desconocido'));
            }
        } catch (err) {
            console.error('Error canceling appointment:', err);
            alert(isBlockEvent ? 'Error al cancelar el bloqueo' : 'Error al cancelar la cita');
        }
    };

    const handleCreateAppointment = async (e) => {
        e.preventDefault();
        if (creatingAppointment) return;

        // Use doctor from form, fallback to selected doctor
        const doctorForAppointment = newAppointment.doctor_id || selectedDoctor;

        if (!doctorForAppointment || doctorForAppointment === 'all') {
            alert('Por favor selecciona un médico para la cita');
            return;
        }

        // Note: We allow creating appointments even if doctor is not connected to Google.
        // The backend will save locally in "offline mode" and sync later when connected.

        if (!newAppointment.is_guest && !newAppointment.patient_id) {
            alert('Por favor selecciona un paciente');
            return;
        }

        if (newAppointment.is_guest && !newAppointment.guest_name.trim()) {
            alert('Por favor ingresa el nombre del invitado');
            return;
        }

        if (newAppointment.start_time >= newAppointment.end_time) {
            alert('La hora de fin debe ser posterior a la hora de inicio');
            return;
        }

        // Prevent creating appointments in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const appointmentDate = new Date(newAppointment.date + 'T00:00:00');
        if (appointmentDate < today) {
            alert('?? No se pueden crear citas en fechas pasadas');
            return;
        }

        try {
            setCreatingAppointment(true);
            const token = localStorage.getItem('token');
            const selectedPatient = patients.find(p => p.id.toString() === newAppointment.patient_id);

            // Generate unique appointment number: CITA-YYYYMMDD-HHMM-XXX
            const now = new Date();
            const dateStr = newAppointment.date.replace(/-/g, '');
            const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
            const uniqueNumber = `CITA-${dateStr}-${random}`;

            // Appointment type label
            const typeLabel = newAppointment.is_guest ? '(Invitado)' : (newAppointment.appointment_type === 'primera_vez' ? '(1ª Vez)' : '(Subsecuente)');
            const patientName = newAppointment.is_guest
                ? newAppointment.guest_name.trim()
                : `${selectedPatient?.first_name || ''} ${selectedPatient?.last_name || ''} ${selectedPatient?.maternal_last_name || ''}`.trim();
            const officeInfo = newAppointment.office ? `Consultorio: ${newAppointment.office}` : '';

            // Validate email to avoid delivery errors
            let patientEmail = selectedPatient?.email || null;
            if (patientEmail) {
                const emailLower = patientEmail.toLowerCase().trim();
                // Filter out common placeholders and invalid emails
                if (emailLower === 'correo@correo.com' ||
                    emailLower === 'email@email.com' ||
                    emailLower === 'test@test.com' ||
                    emailLower.includes('ejemplo') ||
                    !emailLower.includes('@')) {
                    patientEmail = null;
                }
            }

            const eventData = {
                doctor_id: doctorForAppointment,
                patient_id: newAppointment.is_guest ? null : newAppointment.patient_id,
                guest_name: newAppointment.is_guest ? newAppointment.guest_name : null,
                guest_phone: newAppointment.is_guest ? newAppointment.guest_phone : null,
                title: `${uniqueNumber} ${typeLabel} ${patientName}`,
                description: `Número de consulta: ${uniqueNumber}\nTipo: ${newAppointment.is_guest ? 'Invitado' : (newAppointment.appointment_type === 'primera_vez' ? 'Primera Vez' : 'Subsecuente')}\nPaciente: ${patientName}\nTeléfono Invitado: ${newAppointment.is_guest ? newAppointment.guest_phone : 'N/A'}\n${officeInfo}\nMotivo: ${newAppointment.reason}\n\nNotas: ${newAppointment.notes || 'Sin notas'}`,
                date: newAppointment.date,
                start_time: newAppointment.start_time,
                end_time: newAppointment.end_time,
                patient_email: null, // User requested no email to patient
                office: newAppointment.office || null
            };

            const response = await fetch(`${API_URL}/google/events`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(eventData)
            });

            if (response.ok) {
                const responseData = await response.json();

                setShowNewAppointmentModal(false);
                setNewAppointment({
                    doctor_id: '',
                    patient_id: '',
                    patient_name: '',
                    date: '',
                    start_time: '12:00',
                    end_time: '12:30',
                    appointment_type: 'primera_vez',
                    office: '',
                    reason: '',
                    notes: '',
                    is_guest: false,
                    guest_name: '',
                    guest_phone: ''
                });
                setSelectedPatientSummary(null);
                fetchEvents();

                // Show different message for offline vs online mode
                const warningNote = responseData.calendar_warning
                    ? `\n\n⚠️ Aviso: ${responseData.calendar_warning}`
                    : '';

                if (responseData.offline_mode) {
                    alert(`✅ Cita guardada localmente\n\nNúmero: ${uniqueNumber}\n⚠️ No se sincronizó con Google Calendar (sesión expirada). La cita está registrada en el sistema.${warningNote}`);
                } else {
                    alert(`✅ Cita creada exitosamente\n\nNúmero de consulta: ${uniqueNumber}${warningNote}`);
                }
            } else {
                const error = await response.json();
                if (error.conflict && error.block_conflict) {
                    alert('🚫 Agenda Bloqueada:\n' + error.message);
                } else if (error.conflict) {
                    alert('⚠️ Conflicto de Cita:\n' + error.message);
                } else if (response.status === 400 && (error.message || '').toLowerCase().includes('horario')) {
                    alert('⚠️ Fuera de Horario:\n' + error.message);
                } else {
                    alert('Error al crear cita: ' + (error.message || 'Error desconocido'));
                }
            }
        } catch (err) {
            console.error('Error creating appointment:', err);
            alert('Error al crear la cita');
        } finally {
            setCreatingAppointment(false);
        }
    };

    const handleCreateBlock = async (e) => {
        e.preventDefault();
        if (creatingBlock) return;

        if (!newBlock.doctor_id && selectedDoctor === 'all') {
            alert('Por favor selecciona un médico para aplicarle el bloqueo');
            return;
        }

        const doctorForBlock = newBlock.doctor_id || selectedDoctor;

        const startDate = new Date(newBlock.start_date + 'T00:00:00');
        let endDate = new Date(newBlock.end_date + 'T00:00:00');

        if (startDate > endDate) {
            alert('La fecha de fin debe ser igual o posterior a la fecha de inicio');
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (startDate < today) {
            alert('⚠️ No se pueden bloquear fechas pasadas');
            return;
        }

        try {
            setCreatingBlock(true);
            const token = localStorage.getItem('token');
            const datesToBlock = [];
            let currentDate = new Date(startDate);

            while (currentDate <= endDate) {
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                datesToBlock.push(`${year}-${month}-${day}`);
                currentDate.setDate(currentDate.getDate() + 1);
            }

            const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
            const uniqueGroupNumber = `BLOQ-RANGO-${random}`;

            let hasError = false;
            let conflictMessages = [];

            // Execute sequentially — save blocks LOCALLY only (no Google Calendar sync)
            for (const dateStr of datesToBlock) {
                const eventData = {
                    doctor_id: doctorForBlock,
                    patient_id: null,
                    guest_name: 'Bloqueo de Agenda',
                    guest_phone: 'N/A',
                    title: `BLOQUEO - ${newBlock.reason}`,
                    notes: `Grupo de bloqueo: ${uniqueGroupNumber}\nMotivo: ${newBlock.reason}\n\nNota: Este es un bloque de día completo generado por el sistema.`,
                    appointment_date: dateStr,
                    start_time: '00:00:00',
                    end_time: '23:59:59',
                    status: 'confirmed',
                    office: null
                };

                const response = await fetch(`${API_URL}/appointments`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(eventData)
                });

                if (!response.ok) {
                    hasError = true;
                    const error = await response.json();
                    if (error.conflict) {
                        conflictMessages.push(`El día ${dateStr} tiene conflictos: ${error.message}`);
                    } else {
                        conflictMessages.push(`Error el día ${dateStr}: ${error.message || 'Error desconocido'}`);
                    }
                }
            }

            if (hasError) {
                if (conflictMessages.length > 0) {
                    setBlockConflictInfo(conflictMessages.join('\n\n'));
                } else {
                    alert('Error al bloquear uno o más días del rango.');
                }
                fetchEvents(); // Refresh to show any days that did succeed
            } else {
                setShowBlockModal(false);
                resetBlockForm();
                fetchEvents();
                alert(`✅ Rango de fechas bloqueado exitosamente\nLos días seleccionados ya no estarán disponibles para citas.`);
            }
        } catch (err) {
            console.error('Error creating block range:', err);
            alert('Error al bloquear el rango de fechas');
        } finally {
            setCreatingBlock(false);
        }
    };
    const navigate = (direction) => {
        if (viewMode === 'day') {
            const newDate = new Date(currentDate);
            newDate.setDate(newDate.getDate() + direction);
            setCurrentDate(newDate);
            setCurrentWeekStart(getWeekStart(newDate));
        } else if (viewMode === 'week') {
            const newDate = new Date(currentWeekStart);
            newDate.setDate(newDate.getDate() + (direction * 7));
            setCurrentWeekStart(newDate);
            setCurrentDate(newDate);
        } else if (viewMode === 'month') {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() + direction);
            setCurrentDate(newDate);
            setCurrentWeekStart(getWeekStart(newDate));
        }
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setCurrentWeekStart(getWeekStart(today));
    };

    const selectDay = (date) => {
        setCurrentDate(date);
        handleViewModeChange('day');
    };

    const getWeekDays = () => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(date.getDate() + i);
            days.push(date);
        }
        return days;
    };

    const getMonthDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        // Add padding for days before the 1st
        const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
        for (let i = startPadding; i > 0; i--) {
            const d = new Date(year, month, 1 - i);
            days.push({ date: d, isCurrentMonth: false });
        }

        // Add days of the month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push({ date: new Date(year, month, i), isCurrentMonth: true });
        }

        // Add padding for days after the last
        const endPadding = 42 - days.length; // 6 rows * 7 days = 42
        for (let i = 1; i <= endPadding; i++) {
            const d = new Date(year, month + 1, i);
            days.push({ date: d, isCurrentMonth: false });
        }

        return days;
    };

    const getHoursOfDay = () => {
        const hours = [];
        for (let i = 6; i <= 23; i++) {
            hours.push(i);
        }
        return hours;
    };

    // Helper function to format date in local timezone (YYYY-MM-DD)
    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getEventsForDay = (date) => {
        const dateStr = formatLocalDate(date);
        return events.filter(event => {
            // Support both Google Calendar format (start.dateTime) and local format (date)
            let eventDate = '';
            if (event.start?.dateTime || event.start?.date) {
                const eventDateTime = event.start.dateTime || event.start.date;
                eventDate = eventDateTime.substring(0, 10);
            } else if (event.date) {
                eventDate = event.date;
            }
            if (!eventDate) return false;
            return eventDate === dateStr;
        });
    };

    const formatTime = (dateTimeStr) => {
        if (!dateTimeStr) return '';
        // If it's just a time string (HH:MM:SS) like from local DB
        if (typeof dateTimeStr === 'string' && dateTimeStr.length <= 8 && dateTimeStr.includes(':')) {
            const [hours, minutes] = dateTimeStr.split(':');
            return `${hours}:${minutes}`;
        }
        // If it's a full ISO string (from Google Calendar)
        const date = new Date(dateTimeStr);
        if (!isNaN(date.getTime())) {
            return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        }
        return dateTimeStr;
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const selectedDoctorName = doctors.find(d => d.id.toString() === selectedDoctor)?.name || '';
    // For 'all', check if ANY doctor is connected. For specific doctor, check their status.
    const isConnected = selectedDoctor === 'all'
        ? Object.values(googleConnectionStatus).some(v => v)
        : googleConnectionStatus[selectedDoctor];
    // For doctor's own connection status (used in doctor's view)
    const myDoctorIsConnected = myDoctorId ? googleConnectionStatus[myDoctorId] : false;

    // Check if user is a receptionist
    const isReceptionist = user && (
        (user.role?.id === 5 || user.role_id === 5) ||
        (user.role?.name || '').toLowerCase().includes('recep')
    );

    return (
        <div className="google-calendar-view">
            {/* Header */}
            <div className="gcv-header">
                <div className="gcv-title">
                    <CalendarIcon size={28} />
                    <h2>{isDoctor ? 'Mi Calendario' : 'Agenda de Citas'}</h2>
                </div>
                <div className="gcv-actions">
                    {(isDoctor || isReceptionist || (!isDoctor && !isReceptionist && user?.role_id === 1)) && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {!isDoctor && (
                                <button
                                    className="btn-new-appointment"
                                    onClick={() => {
                                        resetAppointmentForm();
                                        setShowNewAppointmentModal(true);
                                    }}
                                    disabled={false}
                                    title="Crear nueva cita"
                                >
                                    <Plus size={20} />
                                    Nueva Cita
                                </button>
                            )}
                            <button
                                className="btn-new-appointment"
                                style={{ backgroundColor: '#64748b', color: '#ffffff' }}
                                onClick={() => {
                                    resetBlockForm();
                                    setShowBlockModal(true);
                                }}
                                title="Bloquear un horario en el calendario"
                            >
                                <Clock size={20} />
                                Bloquear Horario
                            </button>
                        </div>
                    )}

                    {/* Schedule Configuration Button */}
                    {(isDoctor || (selectedDoctor && selectedDoctor !== 'all')) && (
                        <button
                            className="btn-settings"
                            onClick={() => setShowScheduleModal(true)}
                            title="Configurar Horarios de Atención"
                            style={{ marginLeft: '8px' }}
                        >
                            <Clock size={20} />
                        </button>
                    )}

                    {!isDoctor && !isReceptionist && (
                        <button
                            className="btn-settings"
                            onClick={() => setShowSettingsModal(true)}
                        >
                            <Settings size={20} />
                        </button>
                    )}
                </div>
            </div>

            {/* Doctor Selector - Only for non-doctors (admin/reception) */}
            {!isDoctor && doctors.length > 1 && (
                <div className="gcv-doctor-selector">
                    <label>
                        <User size={18} />
                        Médico:
                    </label>
                    <select
                        value={selectedDoctor}
                        onChange={(e) => setSelectedDoctor(e.target.value)}
                    >
                        {doctors.map(doc => (
                            <option key={doc.id} value={doc.id}>
                                {doc.name} {googleConnectionStatus[doc.id] ? '✅' : ''}
                            </option>
                        ))}
                    </select>
                    {isConnected && (
                        <span className="connection-badge connected">
                            <CheckCircle size={16} /> Conectado a Google Calendar
                        </span>
                    )}
                    {!isConnected && selectedDoctor && !isReceptionist && (
                        <button
                            className="btn-connect-inline"
                            onClick={() => connectDoctorToGoogle(selectedDoctor)}
                        >
                            <Link2 size={16} /> Conectar
                        </button>
                    )}
                </div>
            )}

            {/* Doctor's own connection status bar */}
            {isDoctor && myDoctorId && (
                <div className="gcv-doctor-selector">
                    <span className="doctor-welcome">
                        <User size={18} />
                        {doctors.find(d => d.id == myDoctorId)?.name || selectedDoctorName}
                    </span>
                    {myDoctorIsConnected ? (
                        <span className="connection-badge connected">
                            <CheckCircle size={16} /> Conectado a Google Calendar
                        </span>
                    ) : (
                        <button
                            className="btn-connect-inline"
                            onClick={() => connectDoctorToGoogle(myDoctorId)}
                        >
                            <Link2 size={16} /> Conectar mi Google Calendar
                        </button>
                    )}
                    {myDoctorIsConnected && (
                        <button
                            className="btn-disconnect-small"
                            onClick={() => disconnectDoctorFromGoogle(myDoctorId)}
                        >
                            Desconectar
                        </button>
                    )}
                </div>
            )}

            {/* Calendar Navigation */}
            <div className="gcv-navigation">
                <div className="nav-left">
                    <button onClick={() => navigate(-1)}>
                        <ChevronLeft size={20} />
                    </button>
                    <span className="current-period">
                        {viewMode === 'day' && currentDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        {viewMode === 'week' && `${currentWeekStart.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} - ${new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                        {viewMode === 'month' && currentDate.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
                    </span>
                    <button onClick={() => navigate(1)}>
                        <ChevronRight size={20} />
                    </button>
                    <button className="btn-today" onClick={goToToday}>
                        Hoy
                    </button>
                </div>
                <div className="nav-right">
                    <div className="view-toggle">
                        <button
                            className={viewMode === 'day' ? 'active' : ''}
                            onClick={() => handleViewModeChange('day')}
                        >
                            Día
                        </button>
                        <button
                            className={viewMode === 'week' ? 'active' : ''}
                            onClick={() => handleViewModeChange('week')}
                        >
                            Semana
                        </button>
                        <button
                            className={viewMode === 'month' ? 'active' : ''}
                            onClick={() => handleViewModeChange('month')}
                        >
                            Mes
                        </button>
                    </div>
                    <button className="btn-refresh" onClick={fetchEvents} disabled={loading}>
                        <RefreshCw size={18} className={loading ? 'spinning' : ''} />
                    </button>
                </div>
            </div>

            {/* Show loading while initial data is being fetched */}
            {initialLoading ? (
                <div className="gcv-not-connected">
                    <RefreshCw size={48} className="spinning" style={{ color: 'var(--primary)' }} />
                    <h3>Cargando calendario...</h3>
                </div>
            ) : isDoctor && !myDoctorId ? (
                <div className="gcv-not-connected">
                    <RefreshCw size={48} className="spinning" style={{ color: 'var(--primary)' }} />
                    <h3>Cargando tu calendario...</h3>
                    <p>Configurando tu perfil de médico automáticamente.</p>
                </div>
            ) : (
                <>
                    {!isConnected && !isReceptionist && (
                        <div className="gcv-offline-banner" style={{
                            background: '#fef3c7',
                            border: '1px solid #f59e0b',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px'
                        }}>
                            <AlertCircle size={20} style={{ color: '#d97706', flexShrink: 0 }} />
                            <span style={{ color: '#92400e', fontSize: '0.9rem' }}>
                                <strong>{selectedDoctorName}</strong> no está conectado a Google Calendar. Las citas se guardarán localmente.
                            </span>
                            <button
                                className="btn-connect-large"
                                onClick={() => connectDoctorToGoogle(selectedDoctor)}
                                disabled={connectingDoctor === selectedDoctor}
                                style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: '0.85rem' }}
                            >
                                {connectingDoctor === selectedDoctor ? 'Conectando...' : 'Conectar'}
                            </button>
                        </div>
                    )}
                    {/* Day View with Time Grid */}
                    {viewMode === 'day' && (
                        <div className="gcv-day-view">
                            {/* Multi-Doctor Column View */}
                            {selectedDoctor === 'all' ? (
                                <div className="multi-doctor-day-view">
                                    {/* Column Headers */}
                                    <div className="doctor-columns-header">
                                        <div className="hour-column-spacer"></div>
                                        {doctors.filter(d => d.id !== 'all').map((doc, idx) => (
                                            <div
                                                key={doc.id}
                                                className="doctor-column-header"
                                                style={{ background: getDoctorColor(idx) }}
                                            >
                                                <User size={16} />
                                                <span>{doc.name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Time Grid with Doctor Columns */}
                                    <div className="multi-doctor-grid">
                                        {/* Hour Labels Column */}
                                        <div className="hour-labels-column">
                                            {getHoursOfDay().map(hour => (
                                                <div key={hour} className="hour-label-cell">
                                                    {`${hour}:00`}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Doctor Columns */}
                                        {doctors.filter(d => d.id !== 'all').map((doc, docIdx) => {
                                            const doctorEvents = events.filter(e => e.doctorId == doc.id);
                                            return (
                                                <div key={doc.id} className="doctor-day-column">
                                                    {/* Hour Cells Background */}
                                                    {getHoursOfDay().map(hour => (
                                                        <div key={hour} className="hour-cell"></div>
                                                    ))}

                                                    {/* Events for this doctor */}
                                                    {doctorEvents.map((event, idx) => {
                                                        let startHour, startMin, endHour, endMin;

                                                        if (event.start?.dateTime) {
                                                            const startDate = new Date(event.start.dateTime);
                                                            const endDate = new Date(event.end.dateTime);
                                                            startHour = startDate.getHours();
                                                            startMin = startDate.getMinutes();
                                                            endHour = endDate.getHours();
                                                            endMin = endDate.getMinutes();
                                                        } else if (event.start_time) {
                                                            // Local appointment format
                                                            const [sH, sM] = event.start_time.split(':').map(Number);
                                                            const [eH, eM] = event.end_time.split(':').map(Number);
                                                            startHour = sH;
                                                            startMin = sM;
                                                            endHour = eH;
                                                            endMin = eM;
                                                        } else {
                                                            return null;
                                                        }

                                                        let startTotalMinutes = (startHour * 60) + startMin;
                                                        const originalEndMinutes = (endHour * 60) + endMin;
                                                        let endTotalMinutes = originalEndMinutes === 0 ? 24 * 60 : originalEndMinutes; // handle 00:00 as midnight

                                                        const dayStartMinutes = 6 * 60;
                                                        const dayEndMinutes = 24 * 60;

                                                        if (endTotalMinutes <= dayStartMinutes || startTotalMinutes >= dayEndMinutes) return null;

                                                        // Clamp start and end times to visible range
                                                        const visibleStartMinutes = Math.max(startTotalMinutes, dayStartMinutes);
                                                        const visibleEndMinutes = Math.min(endTotalMinutes, dayEndMinutes);

                                                        const top = (visibleStartMinutes - dayStartMinutes);
                                                        const height = Math.max(visibleEndMinutes - visibleStartMinutes, 25);

                                                        return (
                                                            <div
                                                                key={idx}
                                                                className="doctor-event"
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: `${top}px`,
                                                                    height: `${height}px`,
                                                                    left: '4px',
                                                                    right: '4px',
                                                                    background: event.summary?.includes('BLOQUEO') ? '#64748b' :
                                                                        (!(event.summary?.startsWith('CITA-') || event.summary?.startsWith('BLOQUEO-'))
                                                                            ? '#94a3b8'
                                                                            : (event.color || getDoctorColor(docIdx))),
                                                                    borderRadius: '4px',
                                                                    padding: '4px 6px',
                                                                    color: 'white',
                                                                    opacity: (!(event.summary?.startsWith('CITA-') || event.summary?.startsWith('BLOQUEO-'))) ? 0.7 : 1,
                                                                    border: (!(event.summary?.startsWith('CITA-') || event.summary?.startsWith('BLOQUEO-'))) ? '1px dashed #cbd5e1' : 'none',
                                                                    overflow: 'hidden',
                                                                    fontSize: '0.75rem',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                                                    zIndex: 10,
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'flex-start'
                                                                }}
                                                                title={`${formatTime(event.start?.dateTime || event.start_time)} - ${formatTime(event.end?.dateTime || event.end_time)}\n${event.summary || 'Sin título'}`}
                                                                onClick={() => setSelectedEventDetails({ ...event, doctorId: doc.id, doctorName: doc.name })}
                                                            >
                                                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                                                    <div style={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                                                        {formatTime(event.start?.dateTime || event.start_time)}
                                                                    </div>
                                                                    <div style={{
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        fontSize: '0.7rem'
                                                                    }}>
                                                                        {event.summary?.includes('BLOQUEO') ? 'Bloqueado' : event.summary?.replace(/CITA-\d+-\d+\s*/, '').replace(/\(1ª Vez\)|\(Subsecuente\)/g, '').trim() || 'Sin título'}
                                                                    </div>
                                                                </div>
                                                                {!(event.local_status === 'completed' || event.local_status === 'cancelled') && !event.summary?.includes('BLOQUEO') && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            cancelAppointment(event.id, event.summary, doc.id);
                                                                        }}
                                                                        style={{
                                                                            background: 'rgba(255,255,255,0.25)',
                                                                            border: 'none',
                                                                            color: 'white',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            borderRadius: '4px',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                            marginLeft: '4px'
                                                                        }}
                                                                        title="Cancelar cita"
                                                                    >
                                                                        <X size={12} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {events.length === 0 && !loading && (
                                        <div className="no-events-multi" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                            Sin citas para este día
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* Single Doctor Day View */
                                <div className="day-layout-container" style={{ position: 'relative', height: '1020px' }}>
                                    {/* Background Grid (Hours) */}
                                    <div className="day-grid-background">
                                        {getHoursOfDay().map(hour => (
                                            <div key={hour} className="hour-row" style={{ height: '60px', borderBottom: '1px solid #f1f5f9', boxSizing: 'border-box' }}>
                                                <div className="hour-label" style={{ height: '60px', position: 'absolute', left: 0, width: '60px', textAlign: 'right', paddingRight: '10px', color: '#64748b', fontSize: '0.8rem' }}>{`${hour}:00`}</div>
                                                <div className="hour-line" style={{ marginLeft: '70px', height: '100%', borderTop: '1px solid #e2e8f0' }}></div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Events Layer */}
                                    <div className="day-events-layer" style={{ position: 'absolute', top: 0, left: '70px', right: 0, bottom: 0 }}>
                                        {events.map((event, idx) => {
                                            let startHour, startMin, endHour, endMin;

                                            if (event.start?.dateTime) {
                                                const startDate = new Date(event.start.dateTime);
                                                const endDate = new Date(event.end.dateTime);
                                                startHour = startDate.getHours();
                                                startMin = startDate.getMinutes();
                                                endHour = endDate.getHours();
                                                endMin = endDate.getMinutes();
                                            } else if (event.start_time) {
                                                // Local appointment format
                                                const [sH, sM] = event.start_time.split(':').map(Number);
                                                const [eH, eM] = event.end_time.split(':').map(Number);
                                                startHour = sH;
                                                startMin = sM;
                                                endHour = eH;
                                                endMin = eM;
                                            } else {
                                                return null;
                                            }

                                            let startTotalMinutes = (startHour * 60) + startMin;
                                            const originalEndMinutes = (endHour * 60) + endMin;
                                            let endTotalMinutes = originalEndMinutes === 0 ? 24 * 60 : originalEndMinutes; // handle 00:00 as midnight

                                            const dayStartMinutes = 6 * 60;
                                            const dayEndMinutes = 24 * 60;

                                            if (endTotalMinutes <= dayStartMinutes || startTotalMinutes >= dayEndMinutes) return null;

                                            // Clamp start and end times to visible range
                                            const visibleStartMinutes = Math.max(startTotalMinutes, dayStartMinutes);
                                            const visibleEndMinutes = Math.min(endTotalMinutes, dayEndMinutes);

                                            const top = (visibleStartMinutes - dayStartMinutes) * 1;
                                            const durationMinutes = (visibleEndMinutes - visibleStartMinutes);
                                            const height = Math.max(durationMinutes * 1, 25);

                                            return (
                                                <div
                                                    key={idx}
                                                    className="calendar-event-absolute"
                                                    onClick={() => setSelectedEventDetails({ ...event, doctorId: selectedDoctor, doctorName: doctors.find(d => d.id == selectedDoctor)?.name })}
                                                    style={{
                                                        position: 'absolute',
                                                        top: `${top}px`,
                                                        height: `${height}px`,
                                                        left: '10px',
                                                        right: '10px',
                                                        background: event.summary?.includes('BLOQUEO') ? '#64748b' :
                                                            (!(event.summary?.startsWith('CITA-') || event.summary?.startsWith('BLOQUEO-'))
                                                                ? '#94a3b8'
                                                                : (event.color || '#4285f4')),
                                                        borderRadius: '6px',
                                                        padding: '4px 8px',
                                                        color: 'white',
                                                        opacity: (!(event.summary?.startsWith('CITA-') || event.summary?.startsWith('BLOQUEO-'))) ? 0.7 : 1,
                                                        border: (!(event.summary?.startsWith('CITA-') || event.summary?.startsWith('BLOQUEO-'))) ? '1px dashed #cbd5e1' : 'none',
                                                        overflow: 'hidden',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        zIndex: 10,
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <div className="event-info">
                                                        <div className="event-time" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                            {formatTime(event.start?.dateTime || event.start_time)} - {formatTime(event.end?.dateTime || event.end_time)}
                                                        </div>
                                                        <div className="event-title" style={{ fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.2 }}>
                                                            {event.summary?.includes('BLOQUEO') ? 'Bloqueado' : (event.summary || 'Sin título')}
                                                        </div>
                                                        {event.doctorName && (
                                                            <div className="event-doctor" style={{ fontSize: '0.7rem', fontStyle: 'italic', opacity: 0.9, marginTop: '2px' }}>
                                                                Dr. {event.doctorName}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {!(event.local_status === 'completed' || event.local_status === 'cancelled') && (
                                                        <button
                                                            className="btn-cancel-event"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                cancelAppointment(event.id, event.summary, event.doctorId);
                                                            }}
                                                            title="Cancelar cita"
                                                            style={{ background: 'rgba(255,255,255,0.2)', padding: '2px', borderRadius: '4px' }}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {events.length === 0 && !loading && (
                                        <div className="no-events-day" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#94a3b8' }}>
                                            Sin citas para este día
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Week View */}
                    {viewMode === 'week' && (
                        <div className="gcv-week-view">
                            <div className="week-header">
                                {getWeekDays().map((day, index) => (
                                    <div
                                        key={index}
                                        className={`week-day-header ${isToday(day) ? 'today' : ''}`}
                                        onClick={() => selectDay(day)}
                                    >
                                        <span className="day-name">
                                            {day.toLocaleDateString('es-MX', { weekday: 'short' })}
                                        </span>
                                        <span className="day-number">
                                            {day.getDate()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="week-body">
                                {getWeekDays().map((day, index) => (
                                    <div
                                        key={index}
                                        className={`week-day-column ${isToday(day) ? 'today' : ''}`}
                                        onClick={() => selectDay(day)}
                                    >
                                        {loading ? (
                                            <div className="loading-events">Cargando...</div>
                                        ) : (
                                            getEventsForDay(day).map((event, eventIndex) => (
                                                <div
                                                    key={eventIndex}
                                                    className="calendar-event"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedEventDetails({ ...event, doctorId: event.doctorId || selectedDoctor, doctorName: event.doctorName || doctors.find(d => d.id == selectedDoctor)?.name });
                                                    }}
                                                    style={{ background: event.summary?.includes('BLOQUEO') ? '#64748b' : event.color, cursor: 'pointer' }}
                                                >
                                                    <div className="event-info">
                                                        <div className="event-time">
                                                            {formatTime(event.start?.dateTime || event.start_time)} - {formatTime(event.end?.dateTime || event.end_time)}
                                                        </div>
                                                        <div className="event-title">
                                                            {event.summary?.includes('BLOQUEO') ? 'Bloqueado' : (event.summary || 'Sin título')}
                                                        </div>
                                                        {event.doctorName && (
                                                            <div className="event-doctor">
                                                                <small>Dr. {event.doctorName}</small>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {!(event.local_status === 'completed' || event.local_status === 'cancelled') && !event.summary?.includes('BLOQUEO') && (
                                                        <button
                                                            className="btn-cancel-event"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                cancelAppointment(event.id, event.summary, event.doctorId || selectedDoctor);
                                                            }}
                                                            title="Cancelar cita"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                        {!loading && getEventsForDay(day).length === 0 && (
                                            <div className="no-events">Sin citas</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Month View */}
                    {viewMode === 'month' && (
                        <div className="gcv-month-view">
                            <div className="month-header">
                                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
                                    <div key={day} className="month-day-name">{day}</div>
                                ))}
                            </div>
                            <div className="month-grid">
                                {getMonthDays().map((dayObj, index) => {
                                    const dayEvents = getEventsForDay(dayObj.date);
                                    const regularAppointments = dayEvents.filter(e => !e.summary?.includes('BLOQUEO'));
                                    const blockEvents = dayEvents.filter(e => e.summary?.includes('BLOQUEO'));

                                    return (
                                        <div
                                            key={index}
                                            className={`month-day ${!dayObj.isCurrentMonth ? 'other-month' : ''} ${isToday(dayObj.date) ? 'today' : ''}`}
                                            onClick={() => selectDay(dayObj.date)}
                                            style={{ display: 'flex', flexDirection: 'column' }}
                                        >
                                            <span className="month-day-number">{dayObj.date.getDate()}</span>
                                            {regularAppointments.length > 0 && (
                                                <div className="month-events-indicator">
                                                    {regularAppointments.length} cita{regularAppointments.length > 1 ? 's' : ''}
                                                </div>
                                            )}
                                            {blockEvents.map((block, bIdx) => (
                                                <div
                                                    key={bIdx}
                                                    className="month-events-indicator"
                                                    style={{
                                                        backgroundColor: '#64748b',
                                                        marginTop: '2px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        width: '100%',
                                                        display: 'block',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    title={block.summary?.replace('BLOQUEO - ', '')}
                                                >
                                                    Bloqueado
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* New Appointment Side Panel */}
                    {
                        showNewAppointmentModal && (
                            <div
                                className={`appointment-side-panel ${isAppointmentPanelMinimized ? 'minimized' : ''}`}
                                style={{ pointerEvents: 'auto' }}
                            >
                                <div className="panel-header">
                                    <h3><Plus size={18} /> Nueva Cita</h3>
                                    <div className="panel-controls">
                                        <button
                                            type="button"
                                            className="btn-minimize"
                                            onClick={() => setIsAppointmentPanelMinimized(!isAppointmentPanelMinimized)}
                                            title={isAppointmentPanelMinimized ? "Expandir" : "Minimizar"}
                                        >
                                            {isAppointmentPanelMinimized ? '?' : '-'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                resetAppointmentForm();
                                                setShowNewAppointmentModal(false);
                                            }}
                                            title="Cerrar"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>
                                {!isAppointmentPanelMinimized && (
                                    <form onSubmit={handleCreateAppointment} className="appointment-form">
                                        {/* Doctor Selector */}
                                        <div className="form-group">
                                            <label>Médico</label>
                                            <select
                                                value={newAppointment.doctor_id}
                                                onChange={(e) => {
                                                    const doctorId = e.target.value;
                                                    setNewAppointment({ ...newAppointment, doctor_id: doctorId });
                                                    fetchDoctorSchedule(doctorId);
                                                }}
                                                required
                                            >
                                                <option value="">Seleccionar médico</option>
                                                {doctors.filter(d => d.id !== 'all').map(d => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.name} {googleConnectionStatus[d.id] ? '✅' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Working Days Indicator */}
                                        {selectedDoctorSchedule.length > 0 && (
                                            <div className="working-days-indicator">
                                                <label>Días de consulta:</label>
                                                <div className="days-grid">
                                                    {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((dayName, index) => {
                                                        const daySchedule = selectedDoctorSchedule.find(s => s.day_of_week === index);
                                                        const isWorkDay = daySchedule && daySchedule.is_active;
                                                        return (
                                                            <span
                                                                key={index}
                                                                className={`day-badge ${isWorkDay ? 'working' : 'off'}`}
                                                                title={isWorkDay ? `${daySchedule.start_time} - ${daySchedule.end_time}` : 'No laboral'}
                                                            >
                                                                {dayName}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="form-group" style={{ position: 'relative' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <label style={{ margin: 0 }}>Paciente</label>
                                                {!newAppointment.patient_id && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setNewAppointment({ ...newAppointment, is_guest: !newAppointment.is_guest, patient_id: '', patient_name: '', patient_code: '', appointment_type: 'primera_vez' });
                                                            setSelectedPatientSummary(null);
                                                            setPatientSearch('');
                                                        }}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#0ea5e9',
                                                            cursor: 'pointer',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '500',
                                                            textDecoration: 'underline'
                                                        }}
                                                    >
                                                        {newAppointment.is_guest ? 'Vincular a Paciente' : 'Agendar como Invitado'}
                                                    </button>
                                                )}
                                            </div>

                                            {newAppointment.is_guest ? (
                                                <div className="guest-inputs" style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Nombre completo del invitado *"
                                                            className="form-control"
                                                            value={newAppointment.guest_name}
                                                            onChange={(e) => setNewAppointment({ ...newAppointment, guest_name: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Teléfono (Opcional)"
                                                            className="form-control"
                                                            value={newAppointment.guest_phone}
                                                            onChange={(e) => setNewAppointment({ ...newAppointment, guest_phone: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            ) : newAppointment.patient_id ? (
                                                <div className="selected-patient-input" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <input
                                                        type="text"
                                                        value={`${newAppointment.patient_code ? newAppointment.patient_code + '  ' : ''}${newAppointment.patient_name}`}
                                                        readOnly
                                                        className="form-control"
                                                        style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', fontWeight: newAppointment.patient_code ? 600 : 400 }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setNewAppointment({ ...newAppointment, patient_id: '', patient_name: '', patient_code: '' });
                                                            setSelectedPatientSummary(null);
                                                            setPatientSearch('');
                                                        }}
                                                        className="btn-clear-patient"
                                                        style={{
                                                            background: '#fee2e2',
                                                            color: '#ef4444',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '0 10px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <X size={18} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="search-input-wrapper" style={{ position: 'relative' }}>
                                                        <input
                                                            type="text"
                                                            value={patientSearch}
                                                            onChange={(e) => {
                                                                setPatientSearch(e.target.value);
                                                                setShowPatientDropdown(true);
                                                            }}
                                                            onFocus={() => setShowPatientDropdown(true)}
                                                            placeholder="Buscar paciente por nombre o teléfono..."
                                                            className="form-control"
                                                            style={{ paddingLeft: '36px' }}
                                                        />
                                                    </div>
                                                    {showPatientDropdown && patientSearch && (
                                                        <div className="patient-dropdown-list" style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            background: 'white',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '6px',
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            zIndex: 100,
                                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                                        }}>
                                                            {patients
                                                                .filter(p => {
                                                                    const fullName = `${p.first_name} ${p.last_name}${p.maternal_last_name ? ' ' + p.maternal_last_name : ''}`.toLowerCase();
                                                                    const searchLower = patientSearch.toLowerCase();
                                                                    return fullName.includes(searchLower) ||
                                                                        (p.phone && p.phone.includes(searchLower)) ||
                                                                        (p.patient_code && p.patient_code.toLowerCase().includes(searchLower));
                                                                })
                                                                .slice(0, 20)
                                                                .map(p => (
                                                                    <div
                                                                        key={p.id}
                                                                        className="patient-option"
                                                                        onClick={() => {
                                                                            const fullName = `${p.first_name} ${p.last_name}${p.maternal_last_name ? ' ' + p.maternal_last_name : ''}`.trim();
                                                                            setNewAppointment({
                                                                                ...newAppointment,
                                                                                patient_id: p.id.toString(), // Ensure string for consistency
                                                                                patient_name: fullName,
                                                                                patient_email: p.email || '',
                                                                                patient_code: p.patient_code || ''
                                                                            });
                                                                            fetchPatientSummary(p.id);
                                                                            setShowPatientDropdown(false);
                                                                            setPatientSearch('');
                                                                        }}
                                                                        style={{
                                                                            padding: '8px 12px',
                                                                            cursor: 'pointer',
                                                                            borderBottom: '1px solid #f1f5f9'
                                                                        }}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            {p.patient_code && (
                                                                                <span style={{
                                                                                    background: '#0a5483',
                                                                                    color: '#fff',
                                                                                    padding: '2px 8px',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '0.75rem',
                                                                                    fontWeight: 'bold',
                                                                                    whiteSpace: 'nowrap',
                                                                                    flexShrink: 0
                                                                                }}>{p.patient_code}</span>
                                                                            )}
                                                                            <div style={{ flex: 1 }}>
                                                                                <div style={{ fontWeight: 500 }}>{p.first_name} {p.last_name} {p.maternal_last_name || ''}</div>
                                                                                {p.phone && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{p.phone}</div>}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            {patients.filter(p => `${p.first_name} ${p.last_name}${p.maternal_last_name ? ' ' + p.maternal_last_name : ''}`.toLowerCase().includes(patientSearch.toLowerCase())).length === 0 && (
                                                                <div style={{ padding: '12px', textAlign: 'center', color: '#64748b' }}>No se encontraron pacientes</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>



                                        {/* Patient History Indicator */}
                                        {newAppointment.is_guest ? (
                                            <div className="patient-history-indicator" style={{ background: '#f8fafc', borderLeft: '4px solid #94a3b8' }}>
                                                <div className="history-badges">
                                                    <span className="history-badge new-patient" style={{ background: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: '6px' }}>
                                                        👤 Cita como Invitado (Sin expediente)
                                                    </span>
                                                </div>
                                            </div>
                                        ) : selectedPatientSummary && (
                                            <div className="patient-history-indicator">
                                                <div className="history-badges">
                                                    <span className={`history-badge ${selectedPatientSummary.has_history ? 'has-history' : 'new-patient'}`}>
                                                        {selectedPatientSummary.has_history ? '⭐ Paciente con historial' : '🌟 Primera vez'}
                                                    </span>
                                                </div>
                                                <div className="history-details">
                                                    <div className="history-item">
                                                        <span className="history-icon">📅</span>
                                                        <span className="history-label">Citas previas:</span>
                                                        <span className={`history-value ${selectedPatientSummary.appointment_count > 0 ? 'has-data' : ''}`}>
                                                            {selectedPatientSummary.appointment_count || 0}
                                                        </span>
                                                    </div>
                                                    <div className="history-item">
                                                        <span className="history-icon">🔬</span>
                                                        <span className="history-label">Estudios:</span>
                                                        <span className={`history-value ${selectedPatientSummary.studies_count > 0 ? 'has-data' : ''}`}>
                                                            {selectedPatientSummary.studies_count || 0}
                                                        </span>
                                                    </div>
                                                    {selectedPatientSummary.last_visit_date && (
                                                        <div className="history-item last-visit">
                                                            <span className="history-icon">⏰</span>
                                                            <span className="history-label">Última visita:</span>
                                                            <span className="history-value">
                                                                {new Date(selectedPatientSummary.last_visit_date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Fecha</label>
                                                <input
                                                    type="date"
                                                    value={newAppointment.date}
                                                    onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {!newAppointment.is_guest && (
                                            <div className="form-group appointment-type-selector">
                                                <label>Tipo de Cita</label>
                                                <div className="radio-group">
                                                    <label className={`radio-option ${newAppointment.appointment_type === 'primera_vez' ? 'selected' : ''} ${selectedPatientSummary?.appointment_count > 0 ? 'disabled-option' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="appointment_type"
                                                            value="primera_vez"
                                                            checked={newAppointment.appointment_type === 'primera_vez'}
                                                            onChange={(e) => setNewAppointment({ ...newAppointment, appointment_type: e.target.value })}
                                                            disabled={selectedPatientSummary?.appointment_count > 0}
                                                        />
                                                        <span>Primera Vez</span>
                                                    </label>
                                                    <label className={`radio-option ${newAppointment.appointment_type === 'subsecuente' ? 'selected' : ''} ${selectedPatientSummary && selectedPatientSummary.appointment_count === 0 ? 'disabled-option' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="appointment_type"
                                                            value="subsecuente"
                                                            checked={newAppointment.appointment_type === 'subsecuente'}
                                                            onChange={(e) => setNewAppointment({ ...newAppointment, appointment_type: e.target.value })}
                                                            disabled={selectedPatientSummary && selectedPatientSummary.appointment_count === 0}
                                                        />
                                                        <span>Subsecuente</span>
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Hora Inicio</label>
                                                <input
                                                    type="time"
                                                    step="900"
                                                    value={newAppointment.start_time}
                                                    onChange={(e) => setNewAppointment({ ...newAppointment, start_time: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Hora Fin</label>
                                                <input
                                                    type="time"
                                                    step="900"
                                                    value={newAppointment.end_time}
                                                    onChange={(e) => setNewAppointment({ ...newAppointment, end_time: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Motivo de la cita</label>
                                            <input
                                                type="text"
                                                value={newAppointment.reason}
                                                onChange={(e) => setNewAppointment({ ...newAppointment, reason: e.target.value })}
                                                placeholder="Ej: Consulta general, Seguimiento..."
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Consultorio</label>
                                            <select
                                                value={newAppointment.office}
                                                onChange={(e) => setNewAppointment({ ...newAppointment, office: e.target.value })}
                                                required
                                            >
                                                <option value="">Seleccione consultorio...</option>
                                                <option value="Consultorio 1">Consultorio 1</option>
                                                <option value="Consultorio 2">Consultorio 2</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Notas adicionales</label>
                                            <textarea
                                                value={newAppointment.notes}
                                                onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                                                placeholder="Información adicional..."
                                                rows={3}
                                            />
                                        </div>
                                        <div className="modal-actions">
                                            <button type="button" onClick={() => {
                                                resetAppointmentForm();
                                                setShowNewAppointmentModal(false);
                                            }} className="btn-cancel">
                                                Cancelar
                                            </button>
                                            <button type="submit" className="btn-submit" disabled={creatingAppointment}>
                                                <CalendarIcon size={18} />
                                                {creatingAppointment ? 'Creando cita...' : 'Crear Cita en Google Calendar'}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )
                    }

                    {/* Schedule Configuration Modal */}
                    {
                        showScheduleModal && (
                            <DoctorScheduleModal
                                doctorId={isDoctor ? myDoctorId : selectedDoctor}
                                doctorName={isDoctor ? selectedDoctorName : (doctors.find(d => d.id == selectedDoctor)?.name || '')}
                                onClose={() => setShowScheduleModal(false)}
                            />
                        )
                    }

                    {/* Settings Modal */}
                    {
                        showSettingsModal && (
                            <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
                                <div className="modal-content" onClick={e => e.stopPropagation()}>
                                    <div className="modal-header google-header">
                                        <h3><Settings size={20} /> Configuración de Google Calendar</h3>
                                        <button onClick={() => setShowSettingsModal(false)}>
                                            <X size={24} />
                                        </button>
                                    </div>
                                    <div className="settings-content">
                                        <p>Conecta el Google Calendar de cada médico para sincronizar sus citas.</p>
                                        <div className="doctors-list">
                                            {doctors.map(doctor => (
                                                <div key={doctor.id} className="doctor-row">
                                                    <div className="doctor-info">
                                                        <User size={20} />
                                                        <span>{doctor.name}</span>
                                                    </div>
                                                    <div className="doctor-status">
                                                        {googleConnectionStatus[doctor.id] ? (
                                                            <>
                                                                <span className="status-connected">
                                                                    <CheckCircle size={18} /> Conectado
                                                                </span>
                                                                <button
                                                                    className="btn-disconnect"
                                                                    onClick={() => disconnectDoctorFromGoogle(doctor.id)}
                                                                >
                                                                    Desconectar
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                className="btn-connect"
                                                                onClick={() => connectDoctorToGoogle(doctor.id)}
                                                                disabled={connectingDoctor === doctor.id}
                                                            >
                                                                {connectingDoctor === doctor.id ? 'Conectando...' : 'Conectar'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="settings-info">
                                            <AlertCircle size={18} />
                                            <span>
                                                Las citas creadas aquí aparecerán automáticamente en el Google Calendar del médico.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {/* Event Details Modal */}
                    {
                        selectedEventDetails && (
                            <div className="event-detail-overlay" onClick={() => setSelectedEventDetails(null)}>
                                <div className="event-detail-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="event-detail-header">
                                        <h3>Detalles de la Cita</h3>
                                        <button onClick={() => setSelectedEventDetails(null)}>
                                            <X size={24} />
                                        </button>
                                    </div>
                                    <div className="event-detail-content">
                                        <div className="detail-row">
                                            <strong>Fecha:</strong>
                                            <span>
                                                {selectedEventDetails.start?.dateTime
                                                    ? new Date(selectedEventDetails.start.dateTime).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                                                    : selectedEventDetails.date
                                                        ? new Date(selectedEventDetails.date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                                                        : 'Fecha no disponible'
                                                }
                                            </span>
                                        </div>
                                        <div className="detail-row">
                                            <strong>Horario:</strong>
                                            <span>{formatTime(selectedEventDetails.start?.dateTime || selectedEventDetails.start_time)} - {formatTime(selectedEventDetails.end?.dateTime || selectedEventDetails.end_time)}</span>
                                        </div>
                                        <div className="detail-row">
                                            <strong>Médico:</strong>
                                            <span>{selectedEventDetails.doctorName || doctors.find(d => d.id == selectedEventDetails.doctorId)?.name || 'No asignado'}</span>
                                        </div>

                                        {/* Parse description for details */}
                                        {(() => {
                                            const desc = selectedEventDetails.description || '';
                                            const lines = desc.split('\n');
                                            const parsedDetails = {};
                                            lines.forEach(line => {
                                                if (line.includes(':')) {
                                                    const [key, ...valueParts] = line.split(':');
                                                    parsedDetails[key.trim().toLowerCase()] = valueParts.join(':').trim();
                                                }
                                            });

                                            const appointmentId = parsedDetails['número de consulta'] ? parsedDetails['número de consulta'].replace('CITA-', '') : null;
                                            const realId = appointmentId ? appointmentId.split('-').pop() : null;
                                            const isBlock = (selectedEventDetails.summary || selectedEventDetails.title || '').toUpperCase().includes('BLOQUEO');

                                            return (
                                                <>
                                                    {parsedDetails['paciente'] && (
                                                        <div className="detail-row">
                                                            <strong>Paciente:</strong>
                                                            <span>{parsedDetails['paciente']}</span>
                                                        </div>
                                                    )}
                                                    {parsedDetails['consultorio'] && (
                                                        <div className="detail-row highlight">
                                                            <strong>Consultorio:</strong>
                                                            <span>{parsedDetails['consultorio']}</span>
                                                        </div>
                                                    )}
                                                    {parsedDetails['tipo'] && (
                                                        <div className="detail-row">
                                                            <strong>Tipo:</strong>
                                                            <span>{parsedDetails['tipo']}</span>
                                                        </div>
                                                    )}
                                                    {parsedDetails['motivo'] && (
                                                        <div className="detail-row">
                                                            <strong>Motivo:</strong>
                                                            <span>{parsedDetails['motivo']}</span>
                                                        </div>
                                                    )}
                                                    {parsedDetails['notas'] && (
                                                        <div className="detail-row">
                                                            <strong>Notas:</strong>
                                                            <span>{parsedDetails['notas']}</span>
                                                        </div>
                                                    )}
                                                    {parsedDetails['número de consulta'] && (
                                                        <div className="detail-row">
                                                            <strong>ID:</strong>
                                                            <span>{parsedDetails['número de consulta']}</span>
                                                        </div>
                                                    )}

                                                    <div className="detail-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                                        {!isDoctor && selectedEventDetails.id && !isBlock && (
                                                            <button
                                                                className="btn-complete-apt"
                                                                disabled={selectedEventDetails.local_status === 'completed' || selectedEventDetails.local_status === 'cancelled'}
                                                                onClick={async () => {
                                                                    try {
                                                                        // Extract date and time for fallback lookup
                                                                        // Google events have start.dateTime or start.date
                                                                        const start = selectedEventDetails.start?.dateTime || selectedEventDetails.start || selectedEventDetails.start_date;

                                                                        let dateStr = '';
                                                                        let timeStr = '';

                                                                        if (start) {
                                                                            const dateObj = new Date(start);
                                                                            const year = dateObj.getFullYear();
                                                                            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                                                            const day = String(dateObj.getDate()).padStart(2, '0');
                                                                            const hours = String(dateObj.getHours()).padStart(2, '0');
                                                                            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                                                                            const seconds = String(dateObj.getSeconds()).padStart(2, '0');

                                                                            dateStr = `${year}-${month}-${day}`;
                                                                            timeStr = `${hours}:${minutes}:${seconds}`;
                                                                        }

                                                                        const doctorId = selectedEventDetails.doctorId;

                                                                        const response = await fetch(`${API_URL}/appointments/google/${selectedEventDetails.id}?doctor_id=${doctorId}&date=${dateStr}&time=${timeStr}&t=${Date.now()}`, {
                                                                            headers: {
                                                                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                                            }
                                                                        });

                                                                        if (response.ok) {
                                                                            const data = await response.json();
                                                                            if (data && data.id) {
                                                                                updateAppointmentStatus(data.id, 'completed', selectedEventDetails.doctorId);
                                                                            } else {
                                                                                alert('Error: No se encontró la ID de la cita local.');
                                                                            }
                                                                        } else {
                                                                            if (response.status === 404) {
                                                                                alert('Esta cita existe en Google Calendar pero NO está sincronizada con el sistema local. No se puede marcar como completada aquí.');
                                                                            } else {
                                                                                alert('Error al buscar la cita en el sistema.');
                                                                            }
                                                                        }
                                                                    } catch (error) {
                                                                        console.error("Error finding appointment:", error);
                                                                        alert('Error de conexión al buscar la cita.');
                                                                    }
                                                                }}
                                                                style={{
                                                                    flex: 1,
                                                                    background: '#00BBBF',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    padding: '0.75rem',
                                                                    borderRadius: '8px',
                                                                    fontWeight: 600,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '0.5rem',
                                                                    cursor: (selectedEventDetails.local_status === 'completed' || selectedEventDetails.local_status === 'cancelled') ? 'not-allowed' : 'pointer',
                                                                    opacity: (selectedEventDetails.local_status === 'completed' || selectedEventDetails.local_status === 'cancelled') ? 0.6 : 1
                                                                }}
                                                            >
                                                                <CheckCircle size={18} />
                                                                {selectedEventDetails.local_status === 'completed' ? 'Completada' : 'Asistió / Completar'}
                                                            </button>
                                                        )}

                                                        {!isDoctor && (
                                                            <button
                                                                className="btn-cancel-detail"
                                                                disabled={selectedEventDetails.local_status === 'completed' || selectedEventDetails.local_status === 'cancelled'}
                                                                onClick={() => {
                                                                    cancelAppointment(selectedEventDetails.id, selectedEventDetails.summary || selectedEventDetails.title, selectedEventDetails.doctorId);
                                                                    setSelectedEventDetails(null);
                                                                }}
                                                                style={{
                                                                    flex: 1,
                                                                    cursor: (selectedEventDetails.local_status === 'completed' || selectedEventDetails.local_status === 'cancelled') ? 'not-allowed' : 'pointer',
                                                                    opacity: (selectedEventDetails.local_status === 'completed' || selectedEventDetails.local_status === 'cancelled') ? 0.6 : 1
                                                                }}
                                                            >
                                                                <X size={16} />
                                                                {selectedEventDetails.local_status === 'cancelled'
                                                                    ? 'Cancelada'
                                                                    : (isBlock ? 'Cancelar Bloqueo' : 'Cancelar Cita')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </>
            )}

            {/* Block Schedule Modal */}
            {showBlockModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h3><Clock size={18} /> Bloquear Horario</h3>
                            <button className="btn-close" onClick={() => setShowBlockModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleCreateBlock}>
                                {!isDoctor && (
                                    <div className="form-group">
                                        <label>Médico</label>
                                        <select
                                            value={newBlock.doctor_id}
                                            onChange={(e) => setNewBlock({ ...newBlock, doctor_id: e.target.value })}
                                            className="form-control"
                                            required
                                        >
                                            <option value="">Seleccione médico...</option>
                                            {doctors.filter(d => d.id !== 'all').map(doc => (
                                                <option key={doc.id} value={doc.id}>{doc.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Fecha Inicio</label>
                                        <input
                                            type="date"
                                            value={newBlock.start_date}
                                            onChange={(e) => setNewBlock({ ...newBlock, start_date: e.target.value })}
                                            className="form-control"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Fecha Fin</label>
                                        <input
                                            type="date"
                                            value={newBlock.end_date}
                                            onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })}
                                            className="form-control"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Motivo</label>
                                    <input
                                        type="text"
                                        value={newBlock.reason}
                                        onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                                        className="form-control"
                                        placeholder="Ej: Vacaciones, Permiso, Horario de comida"
                                        required
                                    />
                                    <small style={{ display: 'block', marginTop: '4px', color: '#64748b' }}>
                                        Este bloque evitará que se le agenden citas al médico en este horario.
                                    </small>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setShowBlockModal(false)} className="btn-cancel">
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-submit" style={{ backgroundColor: '#64748b' }} disabled={creatingBlock}>
                                        {creatingBlock ? 'Guardando bloqueo...' : 'Guardar Bloqueo'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Block Conflict Notification Modal */}
            {blockConflictInfo && (
                <div className="modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="modal-header" style={{ borderBottom: 'none', paddingBottom: 0, justifyContent: 'center' }}>
                            <AlertCircle size={48} color="#d97706" style={{ marginTop: '1rem' }} />
                        </div>
                        <div className="modal-body" style={{ padding: '0 2rem 2rem 2rem' }}>
                            <h3 style={{ margin: '1rem 0', color: '#1e293b' }}>Citas Existentes en el Horario</h3>
                            <p style={{ color: '#64748b', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                                <strong>{blockConflictInfo}</strong><br /><br />
                                Para poder bloquear este horario general de forma segura, primero debes contactar a los pacientes afectados y <strong>reagendar sus citas</strong> en otro horario libre.
                            </p>
                            <button
                                onClick={() => setBlockConflictInfo(null)}
                                className="btn-submit"
                                style={{ width: '100%', backgroundColor: '#d97706', color: 'white', padding: '10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GoogleCalendarView;

async function updateAppointmentStatus(id, newStatus, doctorId) {
    if (!confirm(`¿Está seguro de marcar esta cita como ${newStatus === 'completed' ? 'COMPLETADA' : newStatus}?`)) return;

    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/appointments/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ status: newStatus })
        });

        if (response.ok) {
            alert('Estatus actualizado correctamente');
            window.location.reload();
        } else {
            const data = await response.json();
            alert(data.message || 'Error al actualizar estatus');
        }
    } catch (err) {
        console.error('Error updating status:', err);
        alert('Error de conexión');
    }
}
